import _ from 'underscore';
import appConfig from '@bingads-webui-component-legacy/config/index';
import {
  LabelsBaseColumn,
  RSAAdCustomizerAttributesColumn,
  generateBaseBIColumns,
  getListOfRawBIColumns,
} from '@bingads-webui-campaign-legacy/campaigns-tab-campaigns/grid/columns-config-base';
import {
  getSelectedBidColumns,
} from '@bingads-webui-campaign/ad-groups-grid/src/columns/dynamic-column-config-utils';

const Permissions = appConfig.get('Permissions');
const {
  IsExperimentsEnabled: showExperiment,
  IsManualCpmEnabled,
  IsRSAAdCustomizerEnabled,
  IsPersonalizedOffersEnabled,
  IsVideoAdsForCampaignEnabled,
  IsPersonalizedOffersV3Enabled,
  IsDSAMixedModeCampaignEnabled,
  IsCampaignMultipleLanguageEnabled,
  IsDealEnabledForMsAds,
  dynamic = {},
} = Permissions;
const {
  PortfolioBidStrategy: IsPortfolioBidStrategyEnabled,
  XandrVideoCampaigns,
  CampaignLifetimeBudget,
  MaeLifetimeBudgetExclusion,
} = dynamic;

const isCampaignLifetimeBudgetEnabled = CampaignLifetimeBudget && !MaeLifetimeBudgetExclusion;

export const BidStrategyNameColumn = {
  name: 'BidStrategyName',
  property: ['Campaign/BidStrategyName'],
};

export const DeliveryColumn = {
  name: 'Delivery',
  property: ['DeliveryStatus', 'DeliveryStatusDetails'],
};
export const CampaignColumn = {
  name: 'Campaign',
  property: _.compact([
    'Campaign/Name',
    'Campaign/Id',
    showExperiment && 'Campaign/BaseCampaignName',
  ]),
};
export const AdGroupCashbackPercentColumn = {
  name: 'CashbackPercent',
  property: 'AdGroupCashback/Percent',
};
export const AdGroupCashbackScopeColumn = {
  name: 'CashbackScope',
  property: 'AdGroupCashback/Scope',
};
export const AdGroupCouponColumn = {
  name: 'Coupon',
  property: 'AdGroupCashback/CouponsEnabled',
};
export const AdGroupIdColumn = {
  name: 'AdGroupId',
  property: 'Id',
};
export const AdGroupTypeColumn = {
  name: 'AdGroupType',
  property: 'AdGroupType',
};
export const AdGroupColumn = {
  name: 'AdGroup',
  property: [
    'Name',
    'Id',
    'Campaign/Name',
    'Campaign/Id',
    'Campaign/CampaignType',
    'Campaign/CampaignSubType',
    'Campaign/CampaignCashback',
    'Campaign/CampaignSettings',
    'Medium',
    'StartDate',
    'EndDate',
    'Labels',
    'AdGroupType',
    IsRSAAdCustomizerEnabled && 'RSAAdCustomizerAttributes',
  ],
};
const BidBaseProperties = _.compact([
  'IsBidLandscapeAvailable', 'DisplayBiddingStrategyType',
  IsDealEnabledForMsAds && 'DealMinimumBid',
]);
export const BidStrategyTypeColumn = {
  name: 'BidStrategyType',
  property: ['DisplayBiddingStrategyType', 'Campaign/CampaignSubType'],
};
export const SearchBidColumn = {
  name: 'SearchBid',
  property: ['SearchBid', ...BidBaseProperties],
};
export const CpmBidColumn = {
  name: 'CpmBid',
  property: ['CpmBid', ...BidBaseProperties],
};
export const CpvBidColumn = {
  name: 'CpvBid',
  property: ['CpvBid', ...BidBaseProperties],
};
export const PercentCpcBidColumn = {
  name: 'PercentCpcBid',
  property: ['PercentCpcBid', ...BidBaseProperties],
};
export const CommissionRateColumn = {
  name: 'CommissionRate',
  property: ['CommissionRate', ...BidBaseProperties],
};
export const QualityScoreColumn = {
  name: 'QualityScore',
  property: 'QualityScore/OverallQualityScore',
};
export const MediumColumn = {
  name: 'Medium',
  property: 'Medium',
};
export const StartDateColumn = {
  name: 'StartDate',
  property: _.compact([
    'StartDate',
    isCampaignLifetimeBudgetEnabled && 'Campaign/StartDate',
    isCampaignLifetimeBudgetEnabled && 'Campaign/UseCampaignLevelDates',
  ]),
};
export const EndDateColumn = {
  name: 'EndDate',
  property: _.compact([
    'EndDate',
    isCampaignLifetimeBudgetEnabled && 'Campaign/EndDate',
    isCampaignLifetimeBudgetEnabled && 'Campaign/UseCampaignLevelDates',
  ]),
};
export const StatusIconColumn = {
  name: 'Status',
  property: _.compact([
    'EntityStatus',
    showExperiment && 'Campaign/ExperimentId',
  ]),
};
export const LanguageColumn = {
  name: 'Language',
  property: ['Language', 'Campaign/Languages', 'Campaign/CampaignType'],
};
export const CustomParametersColumn = {
  name: 'CustomParameters',
  property: 'UrlCustomParameters',
};
export const FinalUrlSuffixColumn = {
  name: 'FinalUrlSuffix',
  property: 'FinalUrlSuffix',
};
export const TrackingTemplateColumn = {
  name: 'TrackingTemplate',
  property: 'TrackingUrlTemplate',
};
export const ColumnCallForwarding = {
  name: 'CallForwarding',
  property: 'CallTrackingStatus',
};

export const ColumnManualCalls = {
  name: 'ManualCalls',
  property: 'PhonePerformanceMetrics/ManualCalls',
};
export const ColumnPhoneImpressions = {
  name: 'PhoneImpressions',
  property: 'PhonePerformanceMetrics/PhoneImpressions',
};
export const ColumnPTR = {
  name: 'PTR',
  property: 'PhonePerformanceMetrics/PTR',
};

const allBidColumns = [
  SearchBidColumn,
  CpvBidColumn,
  CpmBidColumn,
  PercentCpcBidColumn,
  CommissionRateColumn,
];

const callDetailColumns = [
  ColumnCallForwarding,
  ColumnManualCalls,
  ColumnPhoneImpressions,
  ColumnPTR,
];

export const getLockedColumns = ({ scope }) => {
  const baseLockedColumns = [
    AdGroupColumn,
    StatusIconColumn,
    !scope.campaignId && CampaignColumn,
  ];
  return _.compact([...baseLockedColumns, ...allBidColumns]);
};

export const getDefaultColumns = () => {
  const baseDefaultColumns = [
    IsDSAMixedModeCampaignEnabled && AdGroupTypeColumn,
    DeliveryColumn,
    IsPortfolioBidStrategyEnabled && BidStrategyNameColumn,
    BidStrategyTypeColumn,
    ...getSelectedBidColumns({
      allBidColumns,
      IsVideoAdsForCampaignEnabled: IsVideoAdsForCampaignEnabled || XandrVideoCampaigns,
      IsManualCpmEnabled,
    }),
    LabelsBaseColumn,
    QualityScoreColumn,
    MediumColumn,
    AdGroupIdColumn,
    StartDateColumn,
    EndDateColumn,
    IsCampaignMultipleLanguageEnabled && LanguageColumn,
    TrackingTemplateColumn,
    CustomParametersColumn,
    FinalUrlSuffixColumn,
    IsRSAAdCustomizerEnabled && RSAAdCustomizerAttributesColumn,
  ];
  return _.compact(baseDefaultColumns);
};

export const getColumnsMap = (params) => {
  const baseColumns = _.compact([
    IsPersonalizedOffersEnabled && AdGroupCashbackPercentColumn,
    IsPersonalizedOffersEnabled && AdGroupCashbackScopeColumn,
    IsPersonalizedOffersV3Enabled && AdGroupCouponColumn,
    IsDSAMixedModeCampaignEnabled && AdGroupTypeColumn,
  ]);
  const lockedColumns = getLockedColumns(params);
  const defaultColumns = getDefaultColumns(params);
  const rawBIColumns = generateBaseBIColumns(getListOfRawBIColumns(params.gridName));
  return _.object(_.map(
    [
      ...baseColumns,
      ...lockedColumns,
      ...defaultColumns,
      ...rawBIColumns,
      ...allBidColumns,
      ...callDetailColumns,
    ],
    column => [column.name, column.property]
  ));
};

const getName = _.property('name');

export const getColumnConfig = (params) => {
  const lockedColumns = getLockedColumns(params);
  const defaultColumns = getDefaultColumns(params);
  const columnsMap = getColumnsMap(params);
  return {
    lockedColumns: _.map(lockedColumns, getName),
    defaultColumns: _.map(defaultColumns, getName),
    columnsMap,
  };
};
